import React from 'react';
import { trackButtonClick, trackWhatsAppClick } from '../utils/analytics';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline';
  href?: string;
  icon?: string;
  trackingLabel?: string;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  className = '', 
  href, 
  icon,
  trackingLabel,
  ...props 
}) => {
  const baseStyles = "inline-flex items-center justify-center px-8 py-3 font-semibold transition-all duration-300 uppercase tracking-wider text-sm";
  
  const variants = {
    primary: "bg-gradient-to-r from-accent-copper to-accent-gold text-white hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] hover:-translate-y-1",
    secondary: "bg-primary-gray text-white border border-gray-700 hover:border-accent-gold hover:text-accent-gold hover:bg-primary-black",
    outline: "bg-transparent border-2 border-accent-gold text-accent-gold hover:bg-accent-gold hover:text-white"
  };

  const handleClick = () => {
    if (trackingLabel) {
      trackButtonClick(trackingLabel);
      if (href?.includes('wa.me')) {
        trackWhatsAppClick(trackingLabel);
      }
    }
  };

  const content = (
    <>
      {children}
      {icon && <i className={`${icon} ml-3`} aria-hidden="true"></i>}
    </>
  );

  if (href) {
    const isExternal = href.startsWith('http') || href.startsWith('//');
    return (
      <a 
        href={href} 
        className={`${baseStyles} ${variants[variant]} ${className}`}
        onClick={handleClick}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        aria-label={typeof children === 'string' ? children : undefined}
      >
        {content}
      </a>
    );
  }

  return (
    <button 
      className={`${baseStyles} ${variants[variant]} ${className}`} 
      onClick={handleClick}
      aria-label={typeof children === 'string' ? children : undefined}
      {...props}
    >
      {content}
    </button>
  );
};